import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';
import { getStoredImpersonatedUserId } from 'store/user/impersonationStorage';
import { WebSocketContext, WebSocketContextType, ConnectionStatus } from './webSocketContextValue';

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('offline');
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const isConnectingRef = useRef(false);
  const wasConnectedRef = useRef(false);

  const getWebSocketUrl = () => import.meta.env.VITE_WEBSOCKET_URL;

  const connect = useCallback(async () => {
    if (!isAuthenticated) return;
    if (socketRef.current || isConnectingRef.current) return;

    isConnectingRef.current = true;
    try {
      // Resolve the URL up front so a misconfiguration surfaces immediately.
      const wsUrl = getWebSocketUrl();

      const newSocket = io(wsUrl, {
        // Callback form: socket.io invokes this before every (re)connection
        // attempt, so a fresh Auth0 access token is fetched each time. Using a
        // static { token } would freeze the initial token and, once it expires,
        // every reconnect would fail (dropping live transcripts / Scribe streams
        // on any network blip) until a full page reload.
        auth: (cb) => {
          // Read impersonation fresh on every (re)connect so admins "acting as"
          // a user get the same effective identity the REST layer uses via the
          // X-Impersonate-User-Id header.
          const impersonateUserId = getStoredImpersonatedUserId() ?? undefined;
          getAccessTokenSilently()
            .then((token) => cb({ token, impersonateUserId }))
            .catch((error) => {
              console.error('Failed to get access token for WebSocket auth:', error);
              cb({ token: '', impersonateUserId });
            });
        },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        path: '/ws/',
        upgrade: true,
        rememberUpgrade: true
      });

      socketRef.current = newSocket;
      setSocket(newSocket);
      isConnectingRef.current = false;

      newSocket.on('connect', () => {
        isConnectingRef.current = false;
        setIsConnected(true);
        setConnectionError(null);
        setConnectionStatus('connected');
        if (wasConnectedRef.current) {
          setReconnectEpoch((e) => e + 1);
        }
        wasConnectedRef.current = true;
      });

      newSocket.on('disconnect', () => {
        setIsConnected(false);
        setConnectionStatus('reconnecting');
      });

      newSocket.io.on('reconnect_attempt', () => {
        setConnectionStatus('reconnecting');
      });

      newSocket.on('connect_error', (error) => {
        setIsConnected(false);
        setConnectionStatus('reconnecting');
        const message =
          error.message?.includes('websocket') || error.message?.includes('transport')
            ? 'WebSocket connection failed. This may be due to an ad blocker like uBlock Origin. Please try disabling it or adding this site to your whitelist.'
            : error.message || 'Connection failed';
        setConnectionError(message);
      });

      newSocket.io.on('reconnect_failed', () => {
        setConnectionError('Max reconnection attempts reached. Please refresh the page.');
        const current = socketRef.current;
        if (current) {
          current.removeAllListeners();
          current.disconnect();
          socketRef.current = null;
          setSocket(null);
        }
        setIsConnected(false);
        setConnectionStatus('offline');
        isConnectingRef.current = false;
      });
    } catch (error) {
      isConnectingRef.current = false;
      setConnectionError('Failed to initialize WebSocket connection.');
      console.error('Failed to initialize WebSocket:', error);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const disconnect = useCallback(() => {
    const current = socketRef.current;
    if (current) {
      current.removeAllListeners();
      current.disconnect();
      socketRef.current = null;
    }
    isConnectingRef.current = false;
    setSocket(null);
    setIsConnected(false);
    setConnectionStatus('offline');
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      connect();
      return;
    } else {
      disconnect();
    }
  }, [isAuthenticated, connect, disconnect]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        disconnect();
      }
    };
  }, [disconnect]);

  const joinSession = useCallback(
    (sessionId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit(
          'join-session',
          sessionId,
          (
            ack: { ok: boolean; sessionId: string; serverTime: string; error?: string } | undefined
          ) => {
            if (ack && !ack.ok) {
              console.warn(`join-session ack error for ${sessionId}:`, ack.error);
            }
          }
        );
      }
    },
    [isConnected]
  );

  const leaveSession = useCallback(
    (sessionId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('leave-session', sessionId);
        console.log(`Left session room: ${sessionId}`);
      }
    },
    [isConnected]
  );

  const onTranscriptUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('new-transcript', callback);
      socketRef.current.on('transcript-update', callback);
    }
  }, []);

  const offTranscriptUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('new-transcript', callback);
      socketRef.current.off('transcript-update', callback);
    }
  }, []);

  const onTranscriptDelete = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('transcript-delete', callback);
    }
  }, []);

  const offTranscriptDelete = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('transcript-delete', callback);
    }
  }, []);

  const onMetadataUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('new-metadata', callback);
    }
  }, []);

  const offMetadataUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('new-metadata', callback);
    }
  }, []);

  const onMetadataDelete = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('metadata-delete', callback);
    }
  }, []);

  const offMetadataDelete = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('metadata-delete', callback);
    }
  }, []);

  const onSessionStatusChange = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('session-status-change', callback);
    }
  }, []);

  const offSessionStatusChange = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('session-status-change', callback);
      }
    },
    []
  );

  const onGenerationStatus = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('generation-status', callback);
    }
  }, []);

  const offGenerationStatus = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('generation-status', callback);
    }
  }, []);

  const onOneLinerUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.on('one-liner-update', callback);
    }
  }, []);

  const offOneLinerUpdate = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('one-liner-update', callback);
    }
  }, []);

  const onAIResponse = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-response', callback);
      }
    },
    [isConnected]
  );

  const offAIResponse = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-response', callback);
    }
  }, []);

  const onAIResponseChunk = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-response-chunk', callback);
      }
    },
    [isConnected]
  );

  const offAIResponseChunk = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-response-chunk', callback);
    }
  }, []);

  const onAIResponseDone = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-response-done', callback);
      }
    },
    [isConnected]
  );

  const offAIResponseDone = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-response-done', callback);
    }
  }, []);

  const onAIMutationComplete = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-mutation-complete', callback);
      }
    },
    [isConnected]
  );

  const onAIActionPhase = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-action-phase', callback);
      }
    },
    [isConnected]
  );

  const offAIActionPhase = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-action-phase', callback);
    }
  }, []);

  const onAIActionProposed = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-action-proposed', callback);
      }
    },
    [isConnected]
  );

  const offAIActionProposed = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-action-proposed', callback);
    }
  }, []);

  const onAIActionStatus = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('ai-action-status', callback);
      }
    },
    [isConnected]
  );

  const offAIActionStatus = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-action-status', callback);
    }
  }, []);

  const offAIMutationComplete = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ai-mutation-complete', callback);
    }
  }, []);

  const onCompendiumPageUpdated = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('compendium-page-updated', callback);
      }
    },
    [isConnected]
  );

  const offCompendiumPageUpdated = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current) {
        socketRef.current.off('compendium-page-updated', callback);
      }
    },
    []
  );

  const onBotVoiceStatus = useCallback(
    (callback: (data: Record<string, unknown>) => void) => {
      if (socketRef.current && isConnected) {
        socketRef.current.on('bot-voice-status', callback);
      }
    },
    [isConnected]
  );

  const offBotVoiceStatus = useCallback((callback: (data: Record<string, unknown>) => void) => {
    if (socketRef.current) {
      socketRef.current.off('bot-voice-status', callback);
    }
  }, []);

  const value: WebSocketContextType = useMemo(
    () => ({
      socket,
      isConnected,
      connectionError,
      connectionStatus,
      reconnectEpoch,
      joinSession,
      leaveSession,
      onTranscriptUpdate,
      onTranscriptDelete,
      onMetadataUpdate,
      onMetadataDelete,
      onSessionStatusChange,
      onGenerationStatus,
      onAIResponse,
      onAIResponseChunk,
      onAIResponseDone,
      onAIMutationComplete,
      onAIActionPhase,
      onAIActionProposed,
      onAIActionStatus,
      onCompendiumPageUpdated,
      onBotVoiceStatus,
      offTranscriptUpdate,
      offTranscriptDelete,
      offMetadataUpdate,
      offMetadataDelete,
      offSessionStatusChange,
      offGenerationStatus,
      onOneLinerUpdate,
      offOneLinerUpdate,
      offAIResponse,
      offAIResponseChunk,
      offAIResponseDone,
      offAIMutationComplete,
      offAIActionPhase,
      offAIActionProposed,
      offAIActionStatus,
      offCompendiumPageUpdated,
      offBotVoiceStatus
    }),
    [
      socket,
      isConnected,
      connectionError,
      connectionStatus,
      reconnectEpoch,
      joinSession,
      leaveSession,
      onTranscriptUpdate,
      onTranscriptDelete,
      onMetadataUpdate,
      onMetadataDelete,
      onSessionStatusChange,
      onGenerationStatus,
      onAIResponse,
      onAIResponseChunk,
      onAIResponseDone,
      onAIMutationComplete,
      onAIActionPhase,
      onAIActionProposed,
      onAIActionStatus,
      onCompendiumPageUpdated,
      onBotVoiceStatus,
      offTranscriptUpdate,
      offTranscriptDelete,
      offMetadataUpdate,
      offMetadataDelete,
      offSessionStatusChange,
      offGenerationStatus,
      onOneLinerUpdate,
      offOneLinerUpdate,
      offAIResponse,
      offAIResponseChunk,
      offAIResponseDone,
      offAIMutationComplete,
      offAIActionPhase,
      offAIActionProposed,
      offAIActionStatus,
      offCompendiumPageUpdated,
      offBotVoiceStatus
    ]
  );

  return <WebSocketContext value={value}>{children}</WebSocketContext>;
};
